import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { Validators, ReactiveFormsModule, FormGroup, FormBuilder } from '@angular/forms';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-textarea';
import { ToastrService } from '../../../services/toastr-service';
import { CommonModule } from '@angular/common';
import { WorkOrderService } from '../../../services/work-order.service';
import { RemarkForm } from '../../../form-model/cwo-remark.form.model';
import { MaintenanceOrderRemarkScope } from '../../../models/remark-scope.model';
import { catchError, EMPTY } from 'rxjs';
import { MaintenanceOrderRemark } from '../../../models/maintenance-order-remark.model';
import { ApplicationtTextService } from '../../../services/application-text.service';
import { SharedDataService } from '../../../services/shared-data.service';
import { User } from '../../../models/user.model';
import Predefined_Remarks from '../../../data/predefined-remarks';
import { AuthPolicies } from '../../../auth/auth-policies';
@Component({
  selector: 'app-cwo-remarks',
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './cwo-remarks.component.html',
  styleUrl: './cwo-remarks.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateWorkOrderRemarksComponent implements OnInit {
  @Input() remarkForm!: FormGroup<RemarkForm>;
  @Input() workOrderMode: 'create' | 'view' = 'create';
  @Output() remarkAdded = new EventEmitter<boolean>();
  @Input() workOrderRemarks: MaintenanceOrderRemark[] = [];
  @Input() workOrderId = '';
  @Input() showPredefinedRemarks: boolean = false;
  @Input() workorderRepairMode: string = '';
  @Output() private onFormGroupChange = new EventEmitter<any>();
  postingRemark = signal<boolean>(false);
  predefinedRemarkFilteredByRepairMode = computed(() => {
    return Predefined_Remarks.filter((remark) => remark.modes.length === 0 || remark.modes.includes(this.workorderRepairMode));
  });
  user!: User;
  isExternal = '';

  constructor(
    public workOrderService: WorkOrderService,
    public sharedDataService: SharedDataService,
    private formBuilder: FormBuilder,
    private toastrService: ToastrService,
    private readonly appTextService: ApplicationtTextService,
    public authPolicy: AuthPolicies,
  ) {}

  ngOnInit(): void {
    this.addControlsToFormGroup();
    this.remarkForm.valueChanges.subscribe(() => this.onFormGroupChange.emit(this.remarkForm));
    const userData = localStorage.getItem('user');
    if (userData === null) {
      return;
    }
    this.user = JSON.parse(userData);
  }

  onCheckChange(event: any): void {
    if (event.target.checked) {
      this.remarkForm.controls.saveAsExternal.setValue(true);
      this.isExternal = 'E';
    } else {
      this.remarkForm.controls.saveAsExternal.setValue(false);
      this.isExternal = 'I';
    }
  }

  addControlsToFormGroup(): void {
    if (!this.remarkForm) {
      this.remarkForm = this.formBuilder.group({
        remark: this.formBuilder.nonNullable.control<string>('', Validators.required),
        saveAsExternal: this.formBuilder.control<boolean | null>(null),
      });
    } else {
      this.remarkForm.addControl('remark', this.formBuilder.nonNullable.control<string>(''));
      this.remarkForm.addControl('saveAsExternal', this.formBuilder.control<boolean | null>(null));
    }
  }
  onSaveRemark(): void {
    if (this.remarkForm.valid) {
      const userType: string = this.user.isExternal ? 'E' : 'I';
      const remarkType: string = this.remarkForm.controls.saveAsExternal.value == null ? userType : this.isExternal;
      this.postingRemark.set(true);
      let newRemark: MaintenanceOrderRemark = {
        maintenanceOrderRemarkScope:
          !this.user.isExternal && remarkType === 'I' ? MaintenanceOrderRemarkScope.GSC : MaintenanceOrderRemarkScope.ALL,
        maintenanceOrderRemarkText: this.remarkForm.controls.remark.value,
        createUserId: this.user.username,
        createTimestamp: new Date(),
        maintenanceOrderRemarkType: remarkType,
      };

      this.workOrderService
        .postRemarks(this.workOrderId, [newRemark])
        .pipe(
          catchError(() => {
            this.toastrService.showToastr(this.appTextService.UnableToSaveRemarks, 'error');
            this.postingRemark.set(false);
            return EMPTY;
          }),
        )
        .subscribe((response) => {
          this.postingRemark.set(false);
          this.workOrderRemarks = [...this.workOrderRemarks, newRemark];
          this.remarkForm.controls.remark.setValue('');
          this.remarkForm.controls.saveAsExternal.setValue(null);
          this.sharedDataService.getWorkOrder().subscribe((workorder) => {
            if (workorder) {
              workorder.maintenanceOrderRemarks = this.workOrderRemarks;
            }
          });
          this.remarkAdded.emit(true);
        });
    }
  }

  get sortedWorkOrderRemark(): MaintenanceOrderRemark[] {
    return this.workOrderRemarks.sort((a, b) => new Date(b.createTimestamp ?? 0).getTime() - new Date(a.createTimestamp ?? 0).getTime());
  }
  predefinedRemarkTagClicked(remark: string) {
    this.remarkForm.controls.remark.setValue(remark);
  }
}
